import React, { useCallback, useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@material-ui/lab/Pagination";
import Table from "../table";
import theme from "../../theme";
import {
  getVendorTransactionsAction,
  getVendorProfileAction
} from "../../containers/vendorDashboard/action";
import { moneyValue } from "../../utils";
import Loading from "../loading";
import moment from "moment";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 185px)",
    padding: "45px 90px",
    marginLeft: "220px",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    "& .title": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "24px",
    },
    "& .liveIndicator": {
      display: "flex",
      alignItems: "center",
      color: theme.colors.MintGreen,
      fontSize: "14px",
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      "& .dot": {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: theme.colors.MintGreen,
        marginRight: "8px",
        animation: "$pulse 2s infinite",
      },
    },
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    },
    "100%": {
      opacity: 1,
    },
  },
  transactionItem: {
    backgroundColor: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "10px",
    padding: "20px 30px",
    marginBottom: "15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .left": {
      "& .amount": {
        color: theme.colors.Violet,
        fontSize: "24px",
        fontFamily: theme.fontFamily.MARKPROHEAVY,
        marginBottom: "5px",
        "& .currency": {
          fontSize: "14px",
          position: "relative",
          bottom: "4px",
        },
      },
      "& .details": {
        color: theme.colors.Violet,
        fontSize: "14px",
        fontFamily: theme.fontFamily.MARKPRO,
        "& .transactionId": {
          fontFamily: theme.fontFamily.MARKPROBOLD,
          marginRight: "15px",
        },
      },
    },
    "& .right": {
      textAlign: "right",
      "& .time": {
        color: theme.colors.Violet,
        fontSize: "14px",
        fontFamily: theme.fontFamily.MARKPRO,
        marginBottom: "5px",
      },
      "& .status": {
        display: "inline-block",
        padding: "5px 15px",
        borderRadius: "20px",
        fontSize: "12px",
        fontFamily: theme.fontFamily.MARKPROBOLD,
        textTransform: "uppercase",
        backgroundColor: theme.colors.MintGreen,
        color: theme.colors.Violet,
      },
      "& .netAmount": {
        color: theme.colors.DodgerBlue,
        fontSize: "14px",
        fontFamily: theme.fontFamily.MARKPRO,
        marginTop: "5px",
      },
    },
  },
  newTransactionBadge: {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    backgroundColor: theme.colors.Monza,
    color: theme.colors.White,
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
    marginBottom: "20px",
  },
}));

function VendorTransactions() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const getVendorTransactionsMethod = useCallback(
    (data) => dispatch(getVendorTransactionsAction(data)),
    [dispatch]
  );

  const getVendorProfileMethod = useCallback(
    (data) => dispatch(getVendorProfileAction(data)),
    [dispatch]
  );

  const vendorDashboardReducer = useSelector((state) => state.vendorDashboardReducer);
  const vendorProfile = vendorDashboardReducer?.vendorProfile;
  const vendorTransactions = vendorDashboardReducer?.vendorTransactions || { count: 0, next: null, previous: null, data: [] };

  const [transactions, setTransactions] = useState([]);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [newTransactionsCount, setNewTransactionsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // Initial load and page changes
  useEffect(() => {
    if (vendorProfile?.vendor?.id) {
      getVendorTransactionsMethod({
        page: currentPage,
        page_size: pageSize,
        sort: "desc",
        vendor_id: vendorProfile?.vendor?.id
      });
    }
  }, [getVendorTransactionsMethod, vendorProfile?.vendor?.id, currentPage, pageSize]);

  useEffect(() => {
    getVendorProfileMethod(
      { vendor_id: vendorProfile?.vendor?.id });

  }, [getVendorProfileMethod]);

  // Update transactions when reducer updates
  useEffect(() => {
    const transactionsData = vendorTransactions?.data || [];
    if (transactionsData.length > 0) {
      setTransactions(transactionsData);

      // Check for new transactions (only on page 1)
      if (currentPage === 1) {
        if (lastTransactionId && transactionsData.length > 0) {
          const latestId = transactionsData[0].id;
          if (latestId !== lastTransactionId) {
            const newCount = transactionsData.findIndex(t => t.id === lastTransactionId);
            if (newCount > 0) {
              setNewTransactionsCount(newCount);
            }
          }
        } else if (transactionsData.length > 0) {
          setLastTransactionId(transactionsData[0].id);
        }
      }
    } else {
      setTransactions([]);
    }
  }, [vendorTransactions, lastTransactionId, currentPage]);

  // Update total pages when count changes
  useEffect(() => {
    if (vendorTransactions?.count) {
      setTotalPages(Math.ceil(vendorTransactions.count / pageSize));
    }
  }, [vendorTransactions?.count, pageSize]);

  // Polling setup - only poll on page 1
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const vendorStatus = user.status || "pending";

    // Only poll if status is approved and on page 1
    if (vendorStatus === "approved" && currentPage === 1 && !isPollingRef.current) {
      isPollingRef.current = true;
      pollingIntervalRef.current = setInterval(() => {
        if (vendorProfile?.vendor?.id) {
          getVendorTransactionsMethod({
            page: 1,
            page_size: pageSize,
            sort: "desc",
            vendor_id: vendorProfile?.vendor?.id
          });
        }
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingRef.current = false;
      }
    };
  }, [getVendorTransactionsMethod, vendorProfile?.vendor?.id, currentPage, pageSize]);

  // Reset new transactions count when user views
  useEffect(() => {
    if (newTransactionsCount > 0 && currentPage === 1) {
      const timer = setTimeout(() => {
        setNewTransactionsCount(0);
        if (transactions.length > 0) {
          setLastTransactionId(transactions[0].id);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [newTransactionsCount, transactions, currentPage]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setNewTransactionsCount(0); // Reset new transactions count when changing pages
  };

  const formatTransaction = (transaction) => {
    const grossAmount = transaction.gross_amount || transaction.amount || 0;
    const discount = transaction.charge_amount || 0;
    const netAmount = transaction.net || 0;

    return {
      id: transaction.id,
      transactionId: transaction.transaction_id || transaction.payment_id || transaction.id,
      grossAmount,
      netAmount,
      discount,
      currency: transaction.currency || "NGN",
      timestamp: transaction.created_at || transaction.timestamp,
      status: transaction.status || "completed",
      employeeName: transaction.employee_name,
    };
  };

  return (
    <div className={classes.root}>
      <Loading showLoader={vendorDashboardReducer?.loading && transactions.length === 0} />
      {vendorDashboardReducer?.error?.length ? (
        <div className={"error-container"}>
          {Array.isArray(vendorDashboardReducer.error)
            ? vendorDashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{vendorDashboardReducer.error}</div>}
        </div>
      ) : null}

      <div className={classes.headerContainer}>
        <div className="title">Live Transactions</div>
        <div className="liveIndicator">
          <div className="dot"></div>
          <span>Live Updates</span>
        </div>
      </div>

      {newTransactionsCount > 0 && (
        <div
          style={{
            backgroundColor: theme.colors.MintGreen,
            color: theme.colors.Violet,
            padding: "10px 20px",
            borderRadius: "10px",
            marginBottom: "20px",
            textAlign: "center",
            fontFamily: theme.fontFamily.MARKPROBOLD,
          }}
        >
          {newTransactionsCount} new transaction{newTransactionsCount > 1 ? "s" : ""} received!
        </div>
      )}

      {transactions.length > 0 ? (
        <>
          <div>
            {transactions.map((transaction, index) => {
              const formatted = formatTransaction(transaction);
              const isNew = index < newTransactionsCount && currentPage === 1;

              return (
                <div
                  key={transaction.id}
                  className={classes.transactionItem}
                  style={{ position: "relative" }}
                >
                  {isNew && <div className={classes.newTransactionBadge}>!</div>}
                  <div className="left">
                    <div className="amount">
                      <span className="currency">{formatted.currency}</span>{" "}
                      {moneyValue(formatted.netAmount)}
                    </div>
                    <div className="details">
                      <span className="transactionId">
                        ID: {formatted.transactionId}
                      </span>
                      {formatted.employeeName && (
                        <span style={{ marginLeft: "15px" }}>
                          Received from: <span style={{ fontFamily: theme.fontFamily.MARKPROBOLD }}>{formatted.employeeName}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="right">
                    <div className="time">
                      {moment(formatted.timestamp).format("DD/MM/YYYY HH:mm:ss")}
                    </div>
                    <div className="status">Success</div>
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className={classes.pagination}>
              <Pagination
                count={totalPages}
                page={currentPage}
                showFirstButton
                showLastButton
                onChange={handlePageChange}
              />
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: theme.colors.Violet,
            fontFamily: theme.fontFamily.MARKPRO,
          }}
        >
          No transactions yet. Transactions will appear here as customers make payments.
        </div>
      )}
    </div>
  );
}

export default VendorTransactions;
